import React, { useCallback, useState } from 'react';
import { Box, Tab, Typography } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { WithTranslation, withTranslation } from 'react-i18next';

import TabLabel from './TabLabel';
import TabContent from './TabContent';
import { useOfficeHours } from '../provider';

import { useStyles } from './styles';
import { OfficeHoursTabs } from './interfaces';
import { EMAIL } from '../constants';
import { useMytaverseEvent } from '../../../../../../../modules/dashboard/providers';
import { AnalyticsType } from '../../../../../../../interfaces/analytics';

const getDefaultTab = (slots = 0) => {
  return slots ? OfficeHoursTabs.SCHEDULED : OfficeHoursTabs.UPCOMING;
};

const Tabs = ({ t: translate }: WithTranslation) => {
  const classes = useStyles();
  const { trackEventAnalytics } = useMytaverseEvent();

  const {
    upcomingSlots,
    isSlotsLoading,
    scheduledSlots,
    isScheduledSlotsLoading,
    loadNextPageSlots,
    loadNextPageScheduledSlots,
    upcomingSlotsTotal,
    scheduledSlotsTotal,
  } = useOfficeHours();

  const [tab, setTab] = useState<OfficeHoursTabs>(
    getDefaultTab(scheduledSlotsTotal),
  );
  const handleChange = useCallback(
    (_: React.SyntheticEvent, newValue: OfficeHoursTabs) => {
      setTab(newValue);
      trackEventAnalytics(
        newValue == OfficeHoursTabs.UPCOMING
          ? AnalyticsType.OfficeHoursModalUpcomingOpen
          : AnalyticsType.OfficeHoursModalScheduledOpen,
      );
    },
    [trackEventAnalytics, setTab],
  );

  return (
    <Box sx={classes.root}>
      <TabContext value={tab}>
        <Box sx={classes.tabList}>
          <TabList onChange={handleChange}>
            <Tab
              label={
                <TabLabel
                  label={translate('ek.officeHours.scheduled')}
                  count={scheduledSlotsTotal}
                />
              }
              value={OfficeHoursTabs.SCHEDULED}
              sx={classes.tab}
            />
            <Tab
              label={
                <TabLabel
                  label={translate('ek.officeHours.upcoming')}
                  count={upcomingSlotsTotal}
                />
              }
              value={OfficeHoursTabs.UPCOMING}
              sx={classes.tab}
            />
          </TabList>
        </Box>
        <TabPanel value={OfficeHoursTabs.SCHEDULED} sx={classes.tabPanel}>
          <TabContent
            slots={scheduledSlots}
            isLoading={isScheduledSlotsLoading}
            onClick={
              scheduledSlotsTotal > scheduledSlots.length
                ? loadNextPageScheduledSlots
                : null
            }
            noSlotsText={translate('ek.officeHours.noScheduledSlots')}
          />
        </TabPanel>
        <TabPanel value={OfficeHoursTabs.UPCOMING} sx={classes.tabPanel}>
          <TabContent
            slots={upcomingSlots}
            onClick={
              upcomingSlotsTotal > upcomingSlots.length
                ? loadNextPageSlots
                : null
            }
            isLoading={isSlotsLoading}
            noSlotsText={
              <Typography component="span">
                {translate('ek.officeHours.noUpcomingSlots')}{' '}
                <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
              </Typography>
            }
          />
        </TabPanel>
      </TabContext>
    </Box>
  );
};

export default withTranslation('common')(Tabs);
